import {logUtishaDebug, logUtishaError, logUtishaInfo, logUtishaWarn} from 'src/app/util/log';
import {SessionBreadcrumb} from './session-breadcrumbs';

/**
 * Every reason a republish can be scheduled for. Keep this in sync with
 * the scheduler sites — the union is enforced at every call boundary so
 * new reasons can't be added without being listed here (helps audit the
 * recovery matrix at a glance).
 */
export type PublishRepairReason =
  | 'track-ended'
  | 'device-vanished'
  | 'watchdog-video-stall'
  | 'watchdog-video-source-dead'
  | 'watchdog-audio-dead'
  | 'retry-after-failure'
  | 'post-reconnect-verify-failed';

/**
 * Callback contract the watchdog uses to poll and recover. The watchdog
 * intentionally owns no references to Stream.io, Angular, or the publisher
 * directly — it's a behavior, not a coupling. StreamSessionMedia wires the
 * callbacks in its initialize() step and tears them down in destroy().
 */
export interface MediaWatchdogHooks {
  /** Whether the user *intends* to be publishing video right now. */
  isVideoIntended: () => boolean;
  /** Whether the user *intends* to be publishing audio right now. */
  isAudioIntended: () => boolean;
  /**
   * Milliseconds since the canvas pipeline last drew a frame. Return
   * `Infinity` if the pipeline is not running (don't alarm on stopped).
   * Implementations should read from CanvasRenderingPipeline.lastDrawTimestamp.
   */
  msSinceLastVideoFrame: () => number;
  /**
   * Current microphone track readyState / live-ness signal. Returns `true`
   * if the audio publish path is currently capable of carrying audio.
   */
  isAudioAlive: () => boolean;
  /**
   * Source camera track liveness. Returns `true` when the raw camera track
   * feeding the pipeline is producing — i.e. `readyState === 'live'` and
   * `muted === false`. Returns `true` when no source track exists yet
   * (can't distinguish "not started" from "dead"; only `isVideoIntended`
   * can). The distinction from `msSinceLastVideoFrame` matters on mobile:
   * iOS Safari flips `track.muted = true` on backgrounding while the
   * canvas pipeline keeps redrawing the last decoded frame — so the stall
   * check can't see the failure.
   */
  isVideoAlive: () => boolean;
  /** Schedule camera republish — delegated to StreamSessionMedia. */
  requestCameraRepublish: (reason: PublishRepairReason) => void;
  /** Schedule mic republish — delegated to StreamSessionMedia. */
  requestMicRepublish: (reason: PublishRepairReason) => void;
}

export interface MediaWatchdogOptions {
  /** How often the watchdog wakes up to check health. */
  tickIntervalMs: number;
  /**
   * A video pipeline is considered stalled if no frame has been drawn in
   * this long. Should comfortably exceed the slowest expected frame gap
   * (e.g. 10fps minimum → 100ms per frame; 3000ms is 30 frames of slack).
   */
  videoStallThresholdMs: number;
  /**
   * How long the source camera track must stay `muted`/non-live before
   * we call it dead. Generous enough to ride out the ~hundreds-of-ms gap
   * between the tab becoming visible and iOS actually unmuting the
   * underlying capture source, so we don't republish a track that was
   * about to recover on its own.
   */
  videoMutedSustainedThresholdMs: number;
  /** Grace period after a republish request before the watchdog arms again. */
  recoveryGraceMs: number;
}

const DEFAULT_OPTIONS: MediaWatchdogOptions = {
  tickIntervalMs: 1000,
  videoStallThresholdMs: 3000,
  videoMutedSustainedThresholdMs: 2500,
  recoveryGraceMs: 5000,
};

/**
 * MediaWatchdog polls local media health on a fixed interval and triggers
 * recovery via the host's scheduleCameraRepublish / scheduleMicRepublish
 * methods when it detects a failure mode that *neither* the Stream SDK's
 * own events nor the track-ended listeners catch:
 *
 *  - silent black frame: canvas loop alive, track alive on SDK side, but
 *    lastDrawAt is stale (renderer throw, source video stalled, tab
 *    background throttling the rAF fallback)
 *  - audio went silent: mic track still marked live by the SDK but the
 *    underlying MediaStreamTrack is actually dead
 *
 * The watchdog is deliberately dumb. It observes, and when it sees a
 * problem it hands off to the host's republish scheduler — which has its
 * own budgets and cooldowns. That separation keeps the "detect" and
 * "recover" policies independent and testable in isolation.
 */
export class MediaWatchdog {
  private timerId: ReturnType<typeof setInterval> | null = null;
  private lastRecoveryAt = 0;
  private videoNotAliveSince: number | null = null;
  private readonly options: MediaWatchdogOptions;
  private started = false;

  constructor(
    private readonly hooks: MediaWatchdogHooks,
    options: Partial<MediaWatchdogOptions> = {},
  ) {
    this.options = {...DEFAULT_OPTIONS, ...options};
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    this.lastRecoveryAt = 0;
    this.videoNotAliveSince = null;
    logUtishaInfo(`MediaWatchdog: starting (tick=${this.options.tickIntervalMs}ms, videoStall=${this.options.videoStallThresholdMs}ms, videoMuted=${this.options.videoMutedSustainedThresholdMs}ms)`);
    this.timerId = setInterval(() => this.tick(), this.options.tickIntervalMs);
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;
    if (this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
    logUtishaDebug('MediaWatchdog: stopped');
  }

  /**
   * Force an immediate health check. Used by the lifecycle guardian on
   * visibilitychange / pageshow / online events, where we want to verify
   * faster than the tick interval.
   */
  checkNow(): void {
    if (!this.started) return;
    this.tick();
  }

  private tick(): void {
    try {
      const now = Date.now();
      // Respect the recovery grace period — after a republish request we
      // give the host time to actually republish before looking for faults
      // again. Without this the watchdog would fire again on the next tick
      // and burn the republish budget.
      if (this.lastRecoveryAt && now - this.lastRecoveryAt < this.options.recoveryGraceMs) {
        return;
      }

      // Video stall check — silent black frame detection.
      if (this.hooks.isVideoIntended()) {
        const sinceFrame = this.hooks.msSinceLastVideoFrame();
        if (sinceFrame > this.options.videoStallThresholdMs && Number.isFinite(sinceFrame)) {
          logUtishaWarn(`${SessionBreadcrumb.WATCHDOG_VIDEO_STALL} (${sinceFrame}ms since last frame, threshold=${this.options.videoStallThresholdMs}ms)`);
          this.lastRecoveryAt = now;
          this.videoNotAliveSince = null;
          this.hooks.requestCameraRepublish('watchdog-video-stall');
          return; // Don't chain audio check on the same tick — let grace apply.
        }

        // Source-track liveness check — catches iOS background-suspend, where
        // the canvas keeps drawing the last decoded frame (so `sinceFrame` is
        // small) while `track.muted = true` silently kills new content.
        // Debounced so we don't react to the brief flap as iOS unmutes the
        // capture source after a visibility return.
        if (!this.hooks.isVideoAlive()) {
          if (this.videoNotAliveSince === null) {
            this.videoNotAliveSince = now;
          } else if (now - this.videoNotAliveSince >= this.options.videoMutedSustainedThresholdMs) {
            logUtishaWarn(`${SessionBreadcrumb.WATCHDOG_VIDEO_NOT_ALIVE} for ${now - this.videoNotAliveSince}ms — republishing`);
            this.lastRecoveryAt = now;
            this.videoNotAliveSince = null;
            this.hooks.requestCameraRepublish('watchdog-video-source-dead');
            return;
          }
        } else {
          this.videoNotAliveSince = null;
        }
      } else {
        this.videoNotAliveSince = null;
      }

      // Audio liveness check.
      if (this.hooks.isAudioIntended()) {
        const alive = this.hooks.isAudioAlive();
        if (!alive) {
          logUtishaWarn(`${SessionBreadcrumb.WATCHDOG_AUDIO_NOT_ALIVE} while intended`);
          this.lastRecoveryAt = now;
          this.hooks.requestMicRepublish('watchdog-audio-dead');
          return;
        }
      }
    } catch (e: any) {
      // Never let a watchdog tick throw — the last thing we want is for the
      // health monitor to crash the thing it's monitoring.
      logUtishaError('MediaWatchdog: tick threw', e?.message || e);
    }
  }
}
